import Grid from '@material-ui/core/Grid';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRifm } from 'rifm';
import { IButton } from 'src/common/interfaces/IButton';
import Numpad from 'src/numpad/base';
import { ISalesKeypadModifiers } from 'src/sales-keypad/components/sales-keypad';
import { defaultRifmOptions, RifmArguments } from '../keypad-operation';
import KeypadBottomButtons from './keypad-bottom-buttons/keypad-bottom-buttons';
import KeypadHeader from './keypad-header/keypad-header';
import KeypadInputField from './keypad-input-field/keypad-input-field';
import { keypadStyles } from './keypad-styles';

type Props = IKeypad;
const Keypad: React.FC<Props> = (props: Props): JSX.Element | null => {
  const classes = keypadStyles();
  const {
    salesKeypadModifiers,
    inputFieldText,
    initialInputValue,
    disableManualWeight,
    disableQuantity,
    disableDecimalInput,
    disableSubmitWhenNoInput,
    handleOnInputChange,
    replaceDecimalInput,
  } = props;

  const [keypadValue, setKeypadValue] = useState<string>(
    initialInputValue ? initialInputValue : '',
  );
  const [disableButtons, setDisableButtons] = useState<boolean>(false);

  const resetKeypad = useCallback((): void => {
    setKeypadValue('');
    if (handleOnInputChange) {
      handleOnInputChange('');
    }
  }, [handleOnInputChange]);

  const getInput = useCallback((): string => {
    return keypadValue;
  }, [keypadValue]);

  useEffect((): void => {
    if (disableSubmitWhenNoInput) {
      const input = getInput();
      if (input !== '') {
        setDisableButtons(false);
      } else {
        setDisableButtons(true);
      }
    }
  }, [disableSubmitWhenNoInput, getInput]);

  const inputRef = useRef<HTMLInputElement>(null);
  const rifmOptions = props.formatter ? props.formatter : defaultRifmOptions;
  const rifm = useRifm(rifmOptions(keypadValue, setKeypadValue));

  const submitKeypad = useCallback(async (): Promise<void> => {
    setDisableButtons(true);
    await props.submitButton.onClick(keypadValue);
    resetKeypad();
    setDisableButtons(false);
  }, [resetKeypad, props.submitButton, keypadValue]);

  const isSingleLine = useCallback((): boolean => {
    const label = props.topLabelText || '';
    return !label.includes('\n') && !label.includes('<br />');
  }, [props.topLabelText]);

  const handleKeypadChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    rifm.onChange(e);
    setTimeout(() => {
      const length = inputRef.current?.value?.length ?? 0;
      inputRef.current?.setSelectionRange(length, length);
    }, 0);
    if (handleOnInputChange) {
      handleOnInputChange(e.target.value);
    }
  };

  const setItemCode = (value: string): void => {
    setKeypadValue(value);
  };

  if (!props.visible) {
    return null;
  }

  return (
    <Grid
      container
      direction='column'
      style={props.style}
      className={`${classes.container}`}
      data-testid='keypad-container'
      wrap='nowrap'
    >
      <Grid item>
        <Grid direction='column' className='fullHeight' container>
          {/* Header label area */}
          <KeypadHeader
            topLabelText={props.topLabelText}
            isSingleLine={isSingleLine()}
            handleClose={props.handleClose || undefined}
            keypadTestIdPrefix={props.keypadTestIdPrefix}
          />

          {/* Input and numpad and everything else */}
          <Grid
            container
            direction='column'
            style={{ flexGrow: 1 }}
            item
            className={classes.innerContainer}
          >
            {/* Input Area */}
            {props.noInputField ? (
              undefined
            ) : (
              <KeypadInputField
                keyboardId={props.keyboardId || 'default'}
                inputRef={inputRef}
                inputFieldLabel={inputFieldText}
                itemCode={rifm.value}
                onSubmit={submitKeypad}
                onChange={handleKeypadChange}
                resetKeypad={resetKeypad}
                dataTestId={`${props.keypadTestIdPrefix}keypad-input`}
                inputInstructionsLabel={props.inputInstructionsLabelText || ''}
                isPriceMode={props.isPriceMode || false}
              />
            )}

            {/* The numpad */}
            <Grid style={{ flexGrow: 1 }} item className={`fullWidth`}>
              <Grid container className={`fullWidth fullHeight`}>
                <Grid
                  item
                  className={`${props.noInputField ? '' : classes.numpadGridContainer} fullWidth`}
                >
                  <Numpad
                    keyboardId={props.keyboardId || 'default'}
                    minHeight={props.minHeight}
                    dataTestIdPrefix={props.keypadTestIdPrefix}
                    disableManualWeight={disableManualWeight}
                    disableQuantity={disableQuantity}
                    disableDecimalInput={disableDecimalInput}
                    currentInput={keypadValue}
                    setItemCode={setItemCode}
                    salesKeypadModifiers={salesKeypadModifiers}
                    replaceDecimalInput={replaceDecimalInput}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item container>
        {/* OK Buttons */}
        <KeypadBottomButtons
          submitButton={{
            label: props.submitButton.label,
            onClick: submitKeypad,
            testId: props.submitButton.testId || `${props.keypadTestIdPrefix}submit-keypad-button`,
          }}
          disableButtons={props.submitButton.disabled || disableButtons}
        />
      </Grid>
    </Grid>
  );
};

export interface IKeypad {
  salesKeypadModifiers?: ISalesKeypadModifiers;
  keyboardId?: string;
  noInputField?: boolean;
  minHeight?: string;
  visible?: boolean;
  topLabelText?: string;
  inputFieldText?: string;
  inputInstructionsLabelText?: string;
  initialInputValue?: string;
  submitButton: IButton;
  keypadTestIdPrefix?: string;
  formatter?: (value: string, setValue) => RifmArguments;
  handleClose?: () => void;
  disableManualWeight?: boolean;
  disableQuantity?: boolean;
  disableDecimalInput?: boolean;
  replaceDecimalInput?: boolean;
  disableSubmitWhenNoInput?: boolean;
  isPriceMode?: boolean;
  style?: React.CSSProperties;
  handleOnInputChange?: (inputValue: string) => void;
}

export default React.memo(Keypad);
